/*
 * Copyright (c) 2025-2026 Aegis AO Soft LLC and Alexander Orlov.
 * Confidential and proprietary. Author: Alexander Orlov.
 */

import { Link } from 'react-router-dom';
import { Star, MapPin, Building2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { Car } from '../types';
import { priceUnit, fmtPrice, tierList } from '../utils/pricing';
import { categoryLabel } from '../utils/localized';
import ModelImage from './ModelImage';

export default function CarCard({ car }: { car: Car }) {
  const { t, i18n } = useTranslation();
  const amount = car.priceFullTimePerWeek || car.pricePerWeek || 0;
  const unit = t(`units.${priceUnit(car.pricingPeriod)}`);
  const tiers = tierList(car);
  const catLabel = categoryLabel(car, i18n.language);
  return (
    <Link
      to={`/car/${car.id}`}
      className="group bg-white rounded-2xl overflow-hidden ring-1 ring-gray-200/70 shadow-sm hover:shadow-xl hover:-translate-y-1 transition-all duration-300 block text-gray-900"
    >
      <div className="relative aspect-[4/3] bg-gray-100 overflow-hidden">
        <ModelImage
          carImageFirst
          make={car.brand}
          model={car.model}
          fallback={car.imageURL}
          alt={car.title || `${car.brand ?? ''} ${car.model ?? ''}`.trim()}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        {catLabel && (
          <span
            className="absolute top-2.5 left-2.5 text-[10px] font-semibold px-2 py-0.5 rounded-full text-white shadow-sm"
            style={{ backgroundColor: car.typeColor || '#1565C0' }}
          >
            {catLabel}
          </span>
        )}
        {!!car.rating && (
          <span className="absolute top-2.5 right-2.5 flex items-center gap-1 bg-white/90 backdrop-blur-sm text-gray-900 text-[11px] font-semibold px-2 py-0.5 rounded-full shadow-sm">
            <Star size={11} className="fill-current text-yellow-400" /> {car.rating}
          </span>
        )}
      </div>
      <div className="p-3">
        <h3 className="font-semibold text-[15px] leading-tight truncate text-gray-900">
          {car.title || `${car.brand ?? ''} ${car.model ?? ''}`.trim() || 'Car'}
        </h3>
        {(car.city || car.state) && (
          <div className="mt-1 text-xs text-gray-500 flex items-center gap-1">
            <MapPin size={12} className="shrink-0" />
            <span className="truncate">{[car.city, car.state].filter(Boolean).join(', ')}</span>
          </div>
        )}
        {car.companyName && (
          <div className="mt-1 text-xs text-gray-400 flex items-center gap-1">
            <Building2 size={11} className="shrink-0" /> <span className="truncate">{car.companyName}</span>
          </div>
        )}
        <div className="mt-2.5 pt-2.5 border-t border-gray-100 flex items-center justify-between gap-2">
          {tiers.length > 0 ? (
            <span className="font-bold text-sm text-gray-900 flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              {tiers.map((tr) => (
                <span key={tr.unit}>
                  ${fmtPrice(tr.amount)} <span className="text-[11px] font-normal text-gray-500">/ {t(`units.${tr.unit}`)}</span>
                </span>
              ))}
            </span>
          ) : (
            <span className="font-bold text-sm text-gray-900">
              ${fmtPrice(amount)} <span className="text-[11px] font-normal text-gray-500">/ {unit}</span>
            </span>
          )}
          <span className="btn-primary text-xs py-1.5 px-3.5 shrink-0">{t('card.book')}</span>
        </div>
      </div>
    </Link>
  );
}
